import {cellToBoundary, gridDisk, H3IndexInput, latLngToCell} from 'h3-js'
import {cloneDeep} from 'lodash'
import {
	area,
	bearing,
	bearingToAzimuth,
	bbox,
	bboxPolygon,
	booleanIntersects,
	lineIntersect,
	lineString,
	midpoint,
	point,
	polygon,
	transformScale,
	transformTranslate,
	union,
    lineString as turfLineString,
    multiLineString as turfMultiLineString,
    flattenEach,
    length,
} from '@turf/turf'
import geodesic from 'geographiclib-geodesic'
const geod = geodesic.Geodesic.WGS84
import type { Feature, Geometry, LineString, MultiLineString, Position } from "geojson";
import {
	SRID_4326_DEGREES_PER_METER,
	SAFETY_MARK,
	SIGOPS_MARK,
	THRESHOLD_LEVEL
} from '../constants'

import configureMeasurements from 'convert-units'
import allMeasures from 'convert-units/definitions/all'

import type {
  Feature,
  Geometry,
  LineString,
  MultiLineString,
} from "geojson";



const convert = configureMeasurements(allMeasures)


export const accurateDistance = (start_lngLat, end_lngLat) => {
	const res = geod.Inverse(start_lngLat[1], start_lngLat[0], end_lngLat[1], end_lngLat[0])
	return res.s12
}

export const accurateLength = (coordinates) => {

	if (!Array.isArray(coordinates)) return null

	if (coordinates.length === 0 || coordinates.length === 1) return 0

	return coordinates.reduce((sum, lngLat, idx) => {
		if (!lngLat) return sum
		if (idx === coordinates.length-1) return sum

		const nextLngLat = coordinates[idx+1]
		return sum + accurateDistance(lngLat, nextLngLat)
	}, 0)

}

export const accurateAlong = (coordinates, distance) => {

	if (!Array.isArray(coordinates)) return null

	if (coordinates.length === 0 || coordinates.length === 1 || distance == null) return null

	if (distance === 0) return coordinates[0]
	if (distance >= accurateLength(coordinates)) return coordinates[coordinates.length-1]

	// May implement in future...
	if (distance < 0) return null

	let current_start_lngLat = coordinates[0]
	let distance_remaining = distance
	for (const current_end_lngLat of coordinates.slice(1)) {

		const original_distance_remaining = distance_remaining

		distance_remaining -= accurateLength([
			current_start_lngLat,
			current_end_lngLat,
		])

		if (distance_remaining === 0) {
			return current_end_lngLat
		}
		else if (distance_remaining < 0) {
			const res = geod.Direct(
				current_start_lngLat[1],
				current_start_lngLat[0],
				bearingToAzimuth(bearing(current_start_lngLat, current_end_lngLat)),
				original_distance_remaining
			)
			return [res.lon2, res.lat2]
		}
		else {
			current_start_lngLat = current_end_lngLat
		}

	}

}

export const getLevelThresholdByScore = (score,isSigops?: boolean) => {
    if (!score && score !== 0) return THRESHOLD_LEVEL.LEVEL_0;
    const MARK_LEVEL = isSigops ? SIGOPS_MARK  : SAFETY_MARK

    if (score <= MARK_LEVEL.HIGH) return THRESHOLD_LEVEL.LEVEL_3;

    if (score <= MARK_LEVEL.NORMAL) return THRESHOLD_LEVEL.LEVEL_2;
    
    if (score <= MARK_LEVEL.LOW) return THRESHOLD_LEVEL.LEVEL_1;

    return THRESHOLD_LEVEL.LEVEL_0;
};

export const getSafetyNetworkScoreByLevel = (level) => {
    if (!level) return 100;

    if (level === THRESHOLD_LEVEL.LEVEL_3) return 14;

    if (level === THRESHOLD_LEVEL.LEVEL_2) return 34;

    if (level === THRESHOLD_LEVEL.LEVEL_1) return 49;

    return 100;
};


export const recordsToFeatureCollection = (records, preserve_gps?: boolean) => {
	if (!Array.isArray(records)) records = [records];
	const record_array = Array.isArray(records) ? records : [records];
	const records_length = record_array.length;

	const features = new Array(records_length);
	for (let i = 0; i < records_length; i++) {
		const record = record_array[i];
		const { gps, ...rest } = record ?? {}; // shallow omit gps
		const correct_properties = preserve_gps ? { ...rest, gps } : rest;

		features[i] = {
			type: "Feature",
			id: record?.id,
			geometry: gps,
			properties: {
				...correct_properties,
			},
		};
	}

	return {
		type: "FeatureCollection",
		features,
	};
};

export const recordsToSource = records => {
	return {
        'type': 'geojson',
        'data': recordsToFeatureCollection(region),
        'promoteId': 'id',
    }
}

// Turf's normal lineOffset function ends up being not perpendicular, because it assumes a cartesian plane...
// Here's an alternative adapted from https://github.com/Turfjs/turf/issues/1838#issuecomment-927835876 .
export const lineOffsetPerpendicular = (
	line: GeoJSON.Feature<GeoJSON.LineString> | GeoJSON.Position[],
	distance: number,
	turf_options={units: 'meters'},
	coors_only?: boolean
): GeoJSON.Feature<GeoJSON.LineString> | GeoJSON.Position[] => {
    const lineCoords = coors_only ? line : line.geometry.coordinates
    const transformAngle = distance < 0 ? -90 : 90
    if (distance < 0) distance = -distance

    // Determine new offset coordinates by:
    // 1. translating original line segments
    // 2. to account for spherical curvature, scale the above lines and find their intersections
    // 3. set the final coordinates as the intersection points of the scaled lines.
    const offsetLines = []
    for (let i = 0; i < lineCoords.length - 1; i++) { // Translating each segment of the line to correct position
        const angle = bearing(lineCoords[i], lineCoords[i + 1]) + transformAngle
        const firstPoint = transformTranslate(point(lineCoords[i]), distance, angle, turf_options)?.geometry.coordinates
        const secondPoint = transformTranslate(point(lineCoords[i + 1]), distance, angle, turf_options)?.geometry.coordinates
        offsetLines.push([firstPoint, secondPoint])
    }

    const offsetCoords: GeoJSON.Position[] = [offsetLines[0][0]] // First point inserted
    for (let i = 0; i < offsetLines.length; i++) { // For each translated segment of the initial line
        if (offsetLines[i + 1]) { // If there's another segment after this one
        const firstLine = transformScale(lineString(offsetLines[i]), 2) // transformScale is useful in case the two segment don't have an intersection point
        const secondLine = transformScale(lineString(offsetLines[i + 1]), 2) // Which happen when the resulting offset line is bigger than the initial one
        const intersection = lineIntersect(firstLine, secondLine)
        if (intersection?.features?.[0]) {
            // We're calculating the intersection point between the two translated & scaled segments
            offsetCoords.push(intersection.features[0].geometry.coordinates)
        }
        } else offsetCoords.push(offsetLines[i][1]) // If there's no other segment after this one, we simply push the last point of the line
    }

    return coors_only ? offsetCoords : lineString(offsetCoords)
}


export const getNorthernmostMidpoint = (gps) => {

	if (!gps?.coordinates?.length) {
		console.error('Invalid gps')
		return null
	}

	if (gps.type === 'Point') return gps
	if (gps.type === 'MultiPoint' && gps.coordinates.length === 1) return point(gps.coordinates[0])


	let coordinates

	if (gps.type === 'MultiPoint' || gps.type === 'LineString') {
		coordinates = gps.coordinates
	}
	else if (gps.type === 'MultiLineString') {
		coordinates = gps.coordinates.flat()
	}
	else if (gps.type === 'Polygon') {
		coordinates = gps.coordinates[0].slice(0, -1) // Use coordinates of outer ring. Also, ignore last coordinate since it is a repeat of the 0th coordinate.
	}
	else if (gps.type === 'MultiPolygon') {
		const trimmed_outer_ring_coors = gps.coordinates.map(coors => coors[0].slice(0, -1)) // Use coordinates of outer ring. Also, ignore last coordinate since it is a repeat of the 0th coordinate.
		coordinates = trimmed_outer_ring_coors.flat()
	}
	else {
		console.error('getNorthernmostMidpoint: Feature type not supported.')
		return null
	}


	let p1 = [-Infinity, -Infinity]
	let p2 = [-Infinity, -Infinity]

	coordinates.forEach(([lng, lat]) => {

		const lower_p = (p1[1] < p2[1]) ? p1 : p2

		if (lat > lower_p[1]) {
			lower_p[0] = lng
			lower_p[1] = lat
		}
	})

	return midpoint(point(p1), point(p2))

}

export const calculateAverageBearing = (linestring_or_multilinestring, multilinestring_index) => {

	function sum(values) {
		const validValues = values.filter(v => v !== null && v !== undefined);
		return validValues.reduce((acc, val) => acc + val, 0);
	}

	const coordinates = (multilinestring_index != null) ? linestring_or_multilinestring.coordinates[multilinestring_index] : linestring_or_multilinestring.coordinates
	if (coordinates.length < 2) return null

	let bearings = []
	for (let i = 0; i < coordinates.length - 1; i++) {
		const start = coordinates[i]
		const end = coordinates[i + 1]
		const bearing_value = bearing(start, end)
		bearings.push((bearing_value + 360) % 360)
	}

	const radians = bearings.map(angle => angle * (Math.PI / 180))
	const x = sum(radians.map(r => Math.cos(r))) / bearings.length
	const y = sum(radians.map(r => Math.sin(r))) / bearings.length
	const avgBearing = Math.atan2(y, x) * (180 / Math.PI)
	return (avgBearing + 360) % 360
}

export const getCardinalDirection = (bearing, options) => {
	if (bearing === null) return null
	if (options?.include_ordinal_directions) {
		if (bearing >= 337.5 || bearing < 22.5) return 'NB'
		else if (bearing >= 22.5 && bearing < 67.5) return 'NEB'
		else if (bearing >= 67.5 && bearing < 112.5) return 'EB'
		else if (bearing >= 112.5 && bearing < 157.5) return 'SEB'
		else if (bearing >= 157.5 && bearing < 202.5) return 'SB'
		else if (bearing >= 202.5 && bearing < 247.5) return 'SWB'
		else if (bearing >= 247.5 && bearing < 292.5) return 'WB'
		return 'NWB'
	}
	else {
		if (bearing >= 315 || bearing < 45) return 'NB'
		if (bearing >= 45 && bearing < 135) return 'EB'
		if (bearing >= 135 && bearing < 225) return 'SB'
		return 'WB'
	}
}

export const getAverageCardinalDirection = (linestring_or_multilinestring, options: Record<string, string> = {}) => {
	if (!linestring_or_multilinestring) return null

	return getCardinalDirection(
		calculateAverageBearing(
			linestring_or_multilinestring,
			options?.multilinestring_index,
		),
		options,
	)
}

// Ref: https://h3geo.org/docs/core-library/restable/
export const H3_RES_TO_AVG_HEX_AREA_KM_SQ: Record<number, number> = {
	0: 4_357_449.416078381,
	1: 609_788.441794133,
	2: 86_801.780398997,
	3: 12_393.434655088,
	4: 1_770.347654491,
	5: 252.903858182,
	6: 36.129062164,
	7: 5.161293360,
	8: 0.737327598,
	9: 0.105332513,
	10: 0.015047502,
	11: 0.002149643,
	12: 0.000307092,
	13: 0.000043870,
	14: 0.000006267,
	15: 0.000000895,
}
export const H3_RES_TO_AVG_PENT_AREA_KM_SQ = {
	0: 2_562_182.162955496,
	1: 328_434.586246469,
	2: 44_930.898497879,
	3: 6_315.472267516,
	4: 896.582383141,
	5: 127.785583023,
	6: 18.238749548,
	7: 2.604669397,
	8: 0.372048038,
	9: 0.053147195,
	10: 0.007592318,
	11: 0.001084609,
	12: 0.000154944,
	13: 0.000022135,
	14: 0.000003162,
	15: 0.000000452,
}

export function mergePolygons(polygons: GeoJSON.Polygon[]): GeoJSON.Polygon | GeoJSON.MultiPolygon | null {
	let merged: GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined = polygons[0]
	if (!merged) return null
	for (const polygon of polygons.slice(1)) {
		const result: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null = union(merged, polygon)
		if (!result) return null
		merged = result.geometry
	}
	return merged
}

export function h3CellToPolygon(cell: string, res_level: number) {
	const cell_coors = cellToBoundary(cell, true)
	const cell_polygon = polygon(
		[cell_coors],
		{
			id: cell,
			h3_index: cell,
			res_level: res_level
		},
	)
	return cell_polygon
}

export function getH3CellsForPolygon(
	res_level: number,
	polygon_boundary: GeoJSON.Polygon
): GeoJSON.FeatureCollection {

	const polygon_area_km2 = area(polygon_boundary) / 1_000_000
	const polygon_bbox = bbox(polygon_boundary)

	// If grid-like traversal is inefficient (e.g. thin polygon with a large bbox), then use alternate strategies.
	// TODO

	// Else, start with coarse grid, then refine by checking neighbors of filtered cells.
	const [min_lng, min_lat, max_lng, max_lat] = polygon_bbox
	const STEP_DEGREES =  polygon_area_km2 >= 100_000 ? 0.1
		: polygon_area_km2 >= 1_000 ? 0.01
		: 0.001

	const checked_cells = new Set<H3IndexInput>()
	const cells_to_features: Record<string, GeoJSON.Feature> = {}

	for (let lat = min_lat; lat <= max_lat; lat += STEP_DEGREES) {
		for (let lng = min_lng; lng <= max_lng; lng += STEP_DEGREES) {
			const cell = latLngToCell(lat, lng, res_level)
			if (checked_cells.has(cell)) continue
			checked_cells.add(cell)

			const cell_polygon = h3CellToPolygon(cell, res_level)
			if (booleanIntersects(cell_polygon, polygon_boundary)) {
				cells_to_features[cell] = cell_polygon

				// Add neighboring cells for boundary accuracy
				const neighbors = gridDisk(cell, 2)
				for (let i = 0; i < neighbors.length; i++) {
					const neighbor = neighbors[i]
					if (checked_cells.has(neighbor)) continue
					checked_cells.add(neighbor)

					const neighbor_cell_polygon = h3CellToPolygon(neighbor, res_level)
					if (booleanIntersects(neighbor_cell_polygon, polygon_boundary)) {
						cells_to_features[neighbor] = neighbor_cell_polygon
					}
				}
			}
		}
	}

	return {
		type: 'FeatureCollection',
		features: Object.values(cells_to_features)
	}
}

export function getRectGridForPolygon(
	cell_width: number,
	polygon: GeoJSON.Polygon,
	options?: {
		units?: string
	},
): GeoJSON.FeatureCollection {

	const boundary_bbox = bbox(polygon)
	const [min_lng, min_lat, max_lng, max_lat] = boundary_bbox

	// Default: Assume cell_width is in miles
	let cell_width_degrees: number
	if (options?.units === 'degrees') {
		cell_width_degrees = cell_width
	}
	else {
		cell_width_degrees = convert(cell_width).from('mi').to('m') * SRID_4326_DEGREES_PER_METER
	}

	// Create a grid of "square" cells from NW -> SE
	const grid = []
	let idx = 0
	for (let lat = max_lat; lat > min_lat; lat -= cell_width_degrees) {
		for (let lng = min_lng; lng < max_lng; lng += cell_width_degrees) {
			const cell = bboxPolygon([
				lng,
				lat,
				lng + cell_width_degrees,
				lat - cell_width_degrees
			], {
				properties: {
					id: idx,
					cell_width_degrees,
				},
			})

			if (!booleanIntersects(cell, polygon)) continue

			grid.push(cell)
			idx++
		}
	}
	
	return {
		type: 'FeatureCollection',
		features: grid,
	}
}

export function getRelativeTurnDirection(start_point: [number, number], mid_point: [number, number], end_point: [number, number]) {
	
	const approach_edge_bearing = bearing(start_point, mid_point)
	const turn_bearing = bearing(mid_point, end_point)

	const turn_bearing_relative_to_approach = turn_bearing - approach_edge_bearing
	let degrees_to_turn
	let relative_turn_direction

	switch (true) {
		case (turn_bearing_relative_to_approach === 180 || turn_bearing_relative_to_approach === -180):
			degrees_to_turn = turn_bearing_relative_to_approach
			relative_turn_direction = 'u'
		case (turn_bearing_relative_to_approach >= 0 && turn_bearing_relative_to_approach < 180):
			degrees_to_turn = turn_bearing_relative_to_approach
			relative_turn_direction = 'right'
			break
		case (turn_bearing_relative_to_approach > 180 && turn_bearing_relative_to_approach <= 360):
			degrees_to_turn = 360 - turn_bearing_relative_to_approach
			relative_turn_direction = 'left'
			break
		case (turn_bearing_relative_to_approach < 0 && turn_bearing_relative_to_approach > -180):
			degrees_to_turn = -turn_bearing_relative_to_approach
			relative_turn_direction = 'left'
			break
		case (turn_bearing_relative_to_approach < -180 && turn_bearing_relative_to_approach >= -360):
			degrees_to_turn = 360 + turn_bearing_relative_to_approach
			relative_turn_direction = 'right'
			break
		default:
			console.error('Relative bearing should not be >360 or <-360')
			return
	}

	return {
		relative_turn_direction,
		degrees_to_turn,
	}
}

type EdgeGroupGps = Geometry | Feature<Geometry>;

export function toLineString(edge_group_gps: EdgeGroupGps): Feature<LineString> {
  const geom: Geometry =
    edge_group_gps.type === "Feature" ? edge_group_gps.geometry : edge_group_gps;

  if (geom.type === "LineString") {
    return lineString(geom.coordinates as Position[]) as Feature<LineString>;
  }

  if (geom.type === "MultiLineString") {
    const parts = (geom as MultiLineString).coordinates;

    // Common case in your data: MultiLineString with one part
    if (parts.length === 1) {
      return lineString(parts[0] as Position[]) as Feature<LineString>;
    }

    // If multiple parts exist, choose the longest component
    let bestIdx = 0;
    let bestLen = -Infinity;

    for (let i = 0; i < parts.length; i++) {
      const f = lineString(parts[i] as Position[]) as Feature<LineString>;
      const L = length(f, { units: "kilometers" });
      if (L > bestLen) {
        bestLen = L;
        bestIdx = i;
      }
    }

    return lineString(parts[bestIdx] as Position[]) as Feature<LineString>;
  }

  throw new Error(`Unsupported geometry type: ${geom.type}`);
}

export function lineStringToMultiLineString(lineString: LineString): MultiLineString {
	return {
		type: "MultiLineString",
		coordinates: [lineString.coordinates],
	}
}